import { WebsitePlatformType } from "type/website-platform";

/**
 * The three platforms the IDX setup offers (MAX-4419 V3): WordPress, Wix, and Other, where Other
 * is any HTML site. The stored `websitePlatform` keeps its finer-grained id (squarespace, custom,
 * weebly…); this is the consolidated view of it, shared by the setup wizard and Edit Platform.
 */
class Type {

	public static WORD_PRESS = new Type("wordpress", "WordPress");
	public static WIX = new Type("wix", "Wix");
	public static OTHER = new Type("other", "Other");

	private constructor(
		public readonly id: "wordpress" | "wix" | "other",
		public readonly label: string,
	) {
	}

	public get wordpress() {
		return this === Type.WORD_PRESS;
	}

	public get wix() {
		return this === Type.WIX;
	}

	public get other() {
		return this === Type.OTHER;
	}

	/**
	 * The `websitePlatform` to store for this choice. Other keeps the client's existing
	 * non-WordPress, non-Wix id (so a Squarespace or Weebly client stays one) and otherwise becomes
	 * Custom HTML.
	 */
	public toWebsitePlatform(current: WebsitePlatformType | undefined): WebsitePlatformType {
		if (this.wordpress) {
			return WebsitePlatformType.WORD_PRESS;
		}
		if (this.wix) {
			return WebsitePlatformType.WIX;
		}
		return current && !current.wordpress && !current.wix ? current : WebsitePlatformType.CUSTOM;
	}

	/**
	 * The choice a client is on today. An installed Wix app means Wix whatever `websitePlatform`
	 * says, matching how the IDX page treats `IntegrationType.WIX_APP`.
	 */
	public static fromWebsitePlatform(websitePlatform: WebsitePlatformType | undefined, hasWixApp = false): Type {
		if (hasWixApp || websitePlatform?.wix) {
			return Type.WIX;
		}
		if (websitePlatform?.wordpress) {
			return Type.WORD_PRESS;
		}
		return Type.OTHER;
	}

	public static values(): Type[] {
		return Object.values(Type);
	}

	public static getById(id: string) {
		return this.values().find(value => value.id === id);
	}

}

export { Type as PlatformChoice };
